/* eslint-disable */
import { IconButton, Switch } from '@mui/material';
import { BreakfastDiningOutlined, Delete, Visibility, Add } from '@mui/icons-material';
import {
	useCallback, useEffect, useMemo, useState,
} from 'react';
import InputWithToggle from '@/Components/InputWithToggle/InputWithToggle';
import {
	generateRandomKey, getUnitConvertedQuantity, getUnitConvertedUnit, options
} from '@/Helpers';
import Accompaniments from '@/Pages/Food Products/Accompaniments/Accompaniments';
import PackSizeConfigModal from '@/Components/Modals/PackSizeConfigModal/PackSizeConfigModal';

function ProductVariantsTable(props) {
	const {
		handleDelete,
		variants,
		handleVariantInput,
		state,
		isDisabled,
		measurement,
		unitprop = {},
		handleVariantAccompanimentChange = () => { },
		choosenAccompaniment = () => [],
		onPackSizeChange = () => { },
		onPackagingProductIdChange = () => { },
		// Display name and client variant ID are read-only (masterCreation off, non-INTERNAL user)
		lockMasterFields = false,
	} = props;
	const lockedClass = lockMasterFields ? ' !bg-gray-100 !text-gray-500 !cursor-not-allowed' : '';
	const lockMark = lockMasterFields ? (
		<span className='text-orange-500 font-bold' title='Locked. Contact the UY team to change it.'>*</span>
	) : null;

	const { productUnit, skuTypeVariant, purchaseUnit } = unitprop ?? {};
	const unit = productUnit || state?.fields?.productUnits?.find((item) => item?.isApplied)?.id;
	const pUnit = purchaseUnit || state?.fields?.purchaseUnits?.find((item) => item?.isApplied)?.id;
	const skuType = skuTypeVariant || state?.fields?.productClass?.find((item) => item?.isApplied)?.id;
	const allowTransferSellingPrice = useMemo(() => {
		// return skuType === 'product' || skuType === 'intermediate' || skuType === 'PRODUCT' || skuType === 'INTERMEDIATE';
		return true;
	}, [skuType]);

	// Check if packaging type is selected to hide certain columns
	const isPackaging = useMemo(() => {
		return skuType === 'packaging' || skuType === 'PACKAGING';
	}, [skuType]);

	// State for Pack Size Config Modal
	const [packSizeModalOpen, setPackSizeModalOpen] = useState(false);
	const [selectedProductData, setSelectedProductData] = useState({});
	const [variantPackSizes, setVariantPackSizes] = useState({});
	const [variantPackagingProductIds, setVariantPackagingProductIds] = useState({});

	// Initialize pack sizes and packaging product IDs from API data (packSizeConfig from variants)
	useEffect(() => {
		if (variants && variants.length > 0) {
			const initialPackSizes = {};
			const initialPackagingProductIds = {};
			variants.forEach((variant, index) => {
				if (variant?.packSizeConfig && variant.packSizeConfig.length > 0) {
					// Map API format (packId) to modal format (boxId)
					initialPackSizes[index] = variant.packSizeConfig.map(config => ({
						id: config.id.toString() || Math.random(),
						quantity: config.quantity || 0,
						boxId: config.packId.toString() || '',
					}));
				}
				// Initialize packaging product ID from variant data
				if (variant?.packagingProductId) {
					initialPackagingProductIds[index] = String(variant.packagingProductId);
				}
			});
			setVariantPackSizes(initialPackSizes);
			setVariantPackagingProductIds(initialPackagingProductIds);
			// Notify parent about initial pack sizes
			if (Object.keys(initialPackSizes).length > 0) {
				onPackSizeChange(initialPackSizes);
			}
			// Notify parent about initial packaging product IDs
			if (Object.keys(initialPackagingProductIds).length > 0) {
				onPackagingProductIdChange(initialPackagingProductIds);
			}
		}
	}, [variants]);


	const handleOpenPackSizeModal = (variant, index) => {
		setSelectedProductData({
			productName: state?.fields?.name || 'Product',
			productImage: state?.fields?.productImage || state?.fields?.image,
			displayName: variant?.displayName,
			variantIndex: index,
			existingPackSizes: variantPackSizes[index] || [],
			existingPackagingProductId: variantPackagingProductIds[index] || null,
		});
		setPackSizeModalOpen(true);
	};

	const handleClosePackSizeModal = () => {
		setPackSizeModalOpen(false);
		setSelectedProductData({});
	};

	const handleSavePackSizes = (data) => {
		const { variantIndex, packSizes, packagingProductId } = data;
		const newPackSizes = {
			...variantPackSizes,
			[variantIndex]: packSizes,
		};
		setVariantPackSizes(newPackSizes);
		// Update packaging product ID for this variant
		if (packagingProductId !== undefined) {
			const newPackagingProductIds = {
				...variantPackagingProductIds,
				[variantIndex]: packagingProductId ? String(packagingProductId) : '',
			};
			setVariantPackagingProductIds(newPackagingProductIds);
			// Notify parent component of packaging product ID changes
			onPackagingProductIdChange(newPackagingProductIds);
		}
		// Notify parent component of pack size changes
		onPackSizeChange(newPackSizes);
	};

	const getPrice = () => {
		if (measurement === 'US') {
			return '$';
		}
		return '₹';
	};


	const getInputStep = (unit) => {
		if (unit === 'lbs') return 'any';
		if (unit === 'oz' || unit === 'floz' || unit === 'gal') return '.01';
		if (unit === 'kg') return '.001';
		return '';
	};

	return (
		<div className='w-full overflow-auto'>
			<table className='w-full border-collapse'>
				<thead>
					<tr className='bg-[#c4c4c4]'>
						<th className='text-[14px]'>Size</th>
						<th className='text-[14px]'>Default</th>
						<th className='text-[14px]'>Enable</th>
						<th className='text-[14px]'>UY Variant ID</th>
						<th className='text-[14px]'>
							Display Name
							{lockMark}
						</th>
						<th className='text-[14px]'>
							Client Variant ID
							{lockMark}
						</th>
						{!isPackaging && <th className='text-[14px]'>Item Short Code</th>}
						{unit === 'count' && <th className='text-[14px]'>Specified Weight</th>}
						{unit === 'count' && <th className='text-[14px]'>Tolerance Weight</th>}
						{!isPackaging && <th className='text-[14px]'>Container Weight</th>}
						{!isPackaging && <th className='text-[14px]'>Serving Qty(%)</th>}
						{!isPackaging && <th className='text-[14px]'>Yield</th>}
						<th className='text-[14px]'>MOQ</th>
						{!isPackaging && <th className='text-[14px]'>GRN Pack Size</th>}
						{!isPackaging && unit == "count" && <th className='text-[14px]'>Pack Size</th>}
						<th className='text-[14px]'>Cost price(₹)/{variants?.[0]?.costPriceUnit}</th>
						{allowTransferSellingPrice && (
							<>
								<th className='text-[14px]'>
									Transfer Price(
									{getPrice()}
									)/{variants?.[0]?.transferPriceUnit}
								</th>
								<th className='text-[14px]'>
									Selling Price(
									{getPrice()}
									)/{variants?.[0]?.sellingPriceUnit}
								</th>
							</>
						)}
						<th className='text-[14px]'>Delete</th>
					</tr>
				</thead>
				<tbody className='w-full'>
					{variants?.map((variant, index) => {
						return (
							<>
								<tr className='border-b border-[#c4c4c4]'>
									<td className='text-center p-8 '>
										<label className='w-12 block'>
											Size

											{index + 1}
										</label>
									</td>
									<td className='p-4'>
										<Switch
											key={generateRandomKey().key}
											name='isDefault'
											checked={variant.isDefault}
											onChange={handleVariantInput}
											disabled={isDisabled}
											inputProps={{
												'data-id': index,
											}}
											size='small'
											color='primary'
										/>

									</td>
									<td className='p-4'>
										<Switch
											key={generateRandomKey().key}
											name='isEnabled'
											size='small'
											color='primary'
											onChange={handleVariantInput}
											defaultChecked={variant.isEnabled}
											disabled={isDisabled}
											inputProps={{
												'data-id': index,
											}}
										/>
									</td>
									<td className='p-4'>{variant.id}</td>
									<td className='p-4'>
										<input
											className={`max-w-[60px] text-center border border-[#c4c4c4] rounded-md p-2 font-sm outline-none${lockedClass}`}
											type='text'
											placeholder='Display Name'
											name='displayName'
											required
											value={variant.displayName}
											onInput={handleVariantInput}
											disabled={isDisabled || lockMasterFields}
											maxLength={10}
											data-id={index}
										/>
									</td>
									<td className='p-4'>
										<input
											className={`min-w-[9rem] font-sm max-w-[60px] text-center border border-[#c4c4c4] rounded-md p-2 font-sm outline-none${lockedClass}`}
											type='text'
											placeholder='Client Variant ID'
											name='clientVariantId'
											value={variant?.clientVariantId}
											onInput={handleVariantInput}
											maxLength={25}
											disabled={isDisabled || lockMasterFields}
											data-id={index}
										/>
									</td>
									{!isPackaging && (
										<td className='p-4'>
											<input
												className='min-w-[9rem] font-sm max-w-[60px] text-center border border-[#c4c4c4] rounded-md p-2 font-sm outline-none'
												type='text'
												placeholder='Item Short Code'
												name='itemShortcode'
												value={variant?.itemShortcode}
												onInput={(e) => {
													e.target.value = e.target.value.replace(/[^A-Za-z]/g, '');
													handleVariantInput(e);
												}}
												pattern="[A-Za-z]*"
												maxLength={4}
												disabled={isDisabled}
												data-id={index}
											/>
										</td>
									)}
									{unit === 'count' && (
										<>
											<td className='p-4'>
												<InputWithToggle
													width='75px'
													type='number'
													name='minWeight'
													value={variant.minWeight}
													handleInput={handleVariantInput}
													min={0}
													idx={index}
													hideToggle={true}
													isDefault={true}
													unitValue={variant?.minUnit}
													isDisabled={isDisabled}
													step={getInputStep(
														variants?.minUnit
													)}
													required
												/>
											</td>

											<td className='p-4'>
												<InputWithToggle
													width='75px'
													type='number'
													name='maxWeight'
													value={variant.maxWeight}
													handleInput={handleVariantInput}
													min={0}
													idx={index}
													isDisabled={isDisabled}
													isDefault={true}
													unitValue={variant?.maxUnit}
													step={getInputStep(
														variants?.maxUnit
													)}
													required
												/>
											</td>
										</>
									)}
									{!isPackaging && (
										<td className='p-4'>
											<InputWithToggle
												width='75px'
												type='number'
												name='containerWeight'
												isDisabled={isDisabled}
												value={variant.containerWeight}
												handleInput={handleVariantInput}
												min={0}
												idx={index}
												isDefault={true}
												unitValue={variant?.containerUnit}
												step={
													variants?.containerUnit === 'lbs'
														? 'any'
														: ''
												}
											/>
										</td>
									)}
									{!isPackaging && (
										<td className='p-4'>
											<input
												className='max-w-[60px] text-center border border-[#c4c4c4] rounded-md p-2 font-sm outline-none'
												type='number'
												name='servingQtyPercentage'
												required
												value={variant.servingQtyPercentage}
												onInput={handleVariantInput}
												disabled={isDisabled}
												min={1}
												data-id={index}
											/>
										</td>
									)}
									{!isPackaging && (
										<td className='p-4'>
											<div className='flex flex-col items-center justify-center mx-auto'>
												<span className='text-sm w-[8rem] relative bottom-2'>
													{(pUnit && unit)
														? `1 ${pUnit} = ${variant?.factorPurchaseUnit} ${unit}`
														: ''}
												</span>
												<input
													className='max-w-[60px] text-center border border-[#c4c4c4] rounded-md p-2 font-sm outline-none'
													type='number'
													name='factorPurchaseUnit'
													required
													value={variant.factorPurchaseUnit}
													onInput={handleVariantInput}
													disabled={isDisabled}
													min={1}
													data-id={index}
												/>
											</div>
										</td>
									)}

									<td className='p-4'>
										<div className='flex flex-col items-center justify-center mx-auto'>
											<span className='text-sm w-[8rem] relative bottom-2 text-center'>
												{unit
													? `Values are in ${unit}`
													: ''}
											</span>
											<input
												className='max-w-[60px] text-center border border-[#c4c4c4] rounded-md p-2 font-sm outline-none'
												type='text'
												name='pieces'
												required
												value={variant?.pieces}
												onInput={handleVariantInput}
												disabled={isDisabled}
												min={1}
												defaultValue={1}
												data-id={index}
											/>
										</div>
									</td>
									{!isPackaging && (
										<td className='p-4'>
											<InputWithToggle
												type='number'
												name='packSize'
												required
												value={variant?.packSize}
												handleInput={handleVariantInput}
												idx={index}
												isDefault={true}
												unitValue={variant?.packSizeUnit}
												step='any'
												isDisabled={isDisabled}
												min={0}
											/>
										</td>
									)}
									{!isPackaging && unit == "count" && (
										<td className='p-4'>
											{(isDisabled && !variantPackSizes[index]?.length) ? 'No Pack Size' : (
												<button
													type='button'
													onClick={() => handleOpenPackSizeModal(variant, index)}
													className={`px-3 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap flex items-center gap-1 ${variantPackSizes[index]?.length > 0
														? 'bg-[#4caf50] hover:bg-[#43a047] text-white'
														: 'bg-[#f5d742] hover:bg-[#e6c93a] text-black'
														}`}
												>
													{isDisabled ? (
														<Visibility fontSize='small' />
													) : (
														<Add fontSize='small' />
													)}
													{variantPackSizes[index]?.length > 0
														? `${variantPackSizes[index].length} Pack${variantPackSizes[index].length > 1 ? 's' : ''}`
														: (isDisabled ? 'No Pack Size' : 'Add Pack Size')
													}
												</button>
											)}
										</td>
									)}
									<td className='p-4'>
										<InputWithToggle
											width='75px'
											type='number'
											name='costPrice'
											required
											value={variant?.calculatedRecipeCost ? variant?.calculatedRecipeCost : variant?.costPrice}
											handleInput={handleVariantInput}
											isDefault={true}
											unitValue={`₹/${variant?.costPriceUnit}`}
											min={0}
											idx={index}
											isDisabled={variant?.calculatedRecipeCost ? true : isDisabled}
											step='any'
										/>
										{variant?.calculatedRecipeCost > 0 && (
											<span className='text-[10px] mt-[3px] block'>Calculated from recipe ₹/pc</span>
										)}
									</td>
									{allowTransferSellingPrice && (
										<>
											<td className='p-4'>
												<InputWithToggle
													width='75px'
													type='number'
													name='transferPrice'
													required
													value={variant.transferPrice}
													handleInput={handleVariantInput}
													isDisabled={isDisabled}
													isDefault={true}
													unitValue={`₹/${variant?.transferPriceUnit}`}
													min={0}
													idx={index}
													step='any'
												/>
											</td>
											<td className='p-4'>
												<InputWithToggle
													width='75px'
													type='number'
													name='sellingPrice'
													required
													value={
														variant?.sellingPrice
													}
													handleInput={handleVariantInput}
													isDisabled={isDisabled}
													min={0}
													idx={index}
													isDefault={true}
													unitValue={`₹/${variant?.sellingPriceUnit}`}
													step='any'
												/>
											</td>
										</>
									)}
									<td className='p-4'>
										<IconButton
											onClick={handleDelete}
											data-id={index}
										>
											<Delete />
										</IconButton>
									</td>
								</tr>
								<tr>
									<td colSpan={100}>
										<Accompaniments
											modalMode={`PRODUCT_VARIANTS_${index}`}
											groups={{
												ACCOMPANIMENTS: {
													choices: choosenAccompaniment(index),
												},
											}}
											accompanimentsData={variant?.accompaniments}
											handleAccompanimentChange={(accompaniment) => {
												handleVariantAccompanimentChange(index, variant?.id, accompaniment);
											}}
											isDisabled={isDisabled}
											compMode='variant'
										/>
									</td>
								</tr>
							</>
						);
					})}
				</tbody>
			</table>

			{/* Pack Size Configuration Modal */}
			<PackSizeConfigModal
				open={packSizeModalOpen}
				handleClose={handleClosePackSizeModal}
				handleSave={handleSavePackSizes}
				productData={selectedProductData}
				isViewMode={isDisabled}
				selectedPackagingProductId={variantPackagingProductIds[selectedProductData?.variantIndex] || ''}
				onPackagingProductChange={(productId) => {
					if (selectedProductData?.variantIndex !== undefined) {
						setVariantPackagingProductIds(prev => ({
							...prev,
							[selectedProductData.variantIndex]: productId || '',
						}));
					}
				}}
			/>
		</div>
	);
}

export default ProductVariantsTable;
